import { Injectable, inject } from '@angular/core';
import {
    HttpInterceptor,
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpErrorResponse,
} from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { AuthService } from '../service/auth.service';
import { Auth } from '@angular/fire/auth';
import { AppCheck, getToken } from '@angular/fire/app-check';

// 401 bodies sent by our API's auth middleware. Any other 401 (e.g. a third-party
// error passed through by an endpoint) is not a session problem and must not log the user out.
const SESSION_401_MESSAGES = [
    'Unauthorized User',
    'Unauthorized: No app_check token provided',
    'Invalid App Check token',
];

@Injectable()
export class RequestHeaderInterceptor implements HttpInterceptor {
    private auth: Auth = inject(Auth);
    private appCheck: AppCheck = inject(AppCheck);

    constructor(private authService: AuthService) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const currentUser = this.auth.currentUser;

        if (
            !currentUser ||
            request.url.includes('assets') ||
            request.url.startsWith('https://ipinfo.io/json') ||
            /\.firebaseio\.com\/.+\.json/.test(request.url)
        ) {
            return next.handle(request);
        }

        // Check if token needs refresh before making request
        return from(this.ensureValidToken()).pipe(
            switchMap(() => this.getAppCheckToken()),
            switchMap((appCheckToken: string) => {
                const clonedRequest = request.clone({
                    setHeaders: {
                        'X-Firebase-AppCheck': appCheckToken,
                    },
                });
                return next.handle(clonedRequest);
            }),
            catchError((error: HttpErrorResponse) => {
                if (error.status === 401 && SESSION_401_MESSAGES.includes(error.error?.message)) {
                    // Try to refresh token one more time before logging out
                    return from(this.authService.refreshAuthToken()).pipe(
                        switchMap((token) => {
                            if (token) {
                                // Retry the request with new token
                                return from(this.getAppCheckToken()).pipe(
                                    switchMap((appCheckToken: string) => {
                                        const retryRequest = request.clone({
                                            setHeaders: {
                                                'X-Firebase-AppCheck': appCheckToken,
                                            },
                                        });
                                        return next.handle(retryRequest);
                                    })
                                );
                            } else {
                                this.authService.logout();
                                return throwError(() => error);
                            }
                        }),
                        catchError(() => {
                            this.authService.logout();
                            return throwError(() => error);
                        })
                    );
                }
                return throwError(() => error);
            })
        );
    }

    /**
     * Ensure the auth token is valid before making a request
     * Refreshes token if it's expiring soon
     */
    private async ensureValidToken(): Promise<void> {
        if (this.authService.isTokenExpiringSoon()) {
            console.log('🔄 Token expiring soon, refreshing before request');
            await this.authService.refreshAuthToken();
        }
    }

    private async getAppCheckToken(): Promise<string> {
        const currentUser = this.auth.currentUser;
        if (!currentUser) return '';


        try {
            const appCheckTokenResult = await getToken(this.appCheck, false);
            return appCheckTokenResult.token;
        } catch (error: any) {
            console.log(error)
            if (error?.code === 'appCheck/throttled' || error.includes('appCheck/initial-throttle')) {
                console.warn('🔥 Firebase AppCheck throttled. Reloading page to reset...');
                window.location.reload();
            }
            throw error;
        }
    }
}
